import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Eye, EyeOff, Plus, Trash2, Save, Power, Upload, X, Copy, Instagram } from "lucide-react";
import { api, fmt, errMsg, resolveImg, downloadExport } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Switch } from "@/components/ui/switch";

export default function More() {
  const { isOwner } = useAuth();
  const ownerTabs = ["الأماكن", "الباقات", "الإضافات", "الشروط", "بيانات التحويل", "الردود المحفوظة", "الموظفون", "سجل النشاط", "رابط المنصة", "تصدير البيانات"];
  const recTabs = ["الردود المحفوظة", "سجل النشاط"];
  const tabs = isOwner ? ownerTabs : recTabs;
  const [tab, setTab] = useState(tabs[0]);

  return (
    <div className="space-y-4" data-testid="more-page">
      <h1 className="font-display text-2xl">المزيد</h1>
      <div className="flex gap-2 overflow-x-auto pb-1">
        {tabs.map((t) => (
          <button key={t} data-testid={`tab-${t}`} onClick={() => setTab(t)}
            className={`px-4 py-2 rounded-full text-xs whitespace-nowrap ${tab === t ? "btn-gold" : "card-lux text-muted2"}`}>{t}</button>
        ))}
      </div>
      {tab === "الأماكن" && <Venues />}
      {tab === "الباقات" && <Packages />}
      {tab === "الإضافات" && <Addons />}
      {tab === "الشروط" && <Terms />}
      {tab === "بيانات التحويل" && <TransferSettings />}
      {tab === "الردود المحفوظة" && <Replies canEdit={isOwner} />}
      {tab === "الموظفون" && <Staff />}
      {tab === "سجل النشاط" && <Activity />}
      {tab === "رابط المنصة" && <PlatformLink />}
      {tab === "تصدير البيانات" && <Exports />}
    </div>
  );
}

function useList(key, url) {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: [key], queryFn: async () => (await api.get(url)).data });
  const invalidate = () => qc.invalidateQueries({ queryKey: [key] });
  return { data, invalidate };
}
const run = async (fn, ok, after) => { try { await fn(); toast.success(ok); after && after(); } catch (e) { toast.error(errMsg(e)); } };

// ---------- Venues ----------
function Venues() {
  const { data, invalidate } = useList("admin-venues", "/admin/venues");
  const toggle = (v) => run(() => api.patch(`/admin/venues/${v.id}`, { visible: !v.visible }), "تم التحديث", invalidate);
  const upload = (v, file) => run(async () => { const fd = new FormData(); fd.append("file", file); await api.post(`/admin/venues/${v.id}/images`, fd, { headers: { "Content-Type": "multipart/form-data" } }); }, "تمت إضافة الصورة", invalidate);
  const delImg = (v, ref) => run(() => api.delete(`/admin/venues/${v.id}/images`, { params: { ref } }), "تم حذف الصورة", invalidate);
  const saveSlots = (v, slots) => run(() => api.patch(`/admin/venues/${v.id}`, { time_slots: slots }), "تم حفظ الأوقات", invalidate);
  return (
    <div className="space-y-4" data-testid="venues-manage">
      {(data || []).map((v) => (
        <div key={v.id} className="card-lux p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-offwhite font-medium">{v.name}</p>
              <p className="text-xs text-muted2 flex items-center gap-1">{v.visible ? <><Eye size={12} /> ظاهر للزبائن</> : <><EyeOff size={12} /> مخفي</>}</p>
            </div>
            <label className="flex items-center gap-2 text-xs text-muted2">إظهار للزبائن
              <Switch checked={v.visible} onCheckedChange={() => toggle(v)} data-testid={`venue-toggle-${v.id}`} />
            </label>
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {(v.images || []).map((img, i) => (
              <div key={i} className="relative shrink-0">
                <img src={resolveImg(img)} alt="" className="w-24 h-24 rounded-lg object-cover" />
                <button onClick={() => delImg(v, img)} data-testid={`venue-img-del-${v.id}-${i}`} className="absolute top-1 end-1 w-6 h-6 rounded-full bg-black/70 text-red-300 flex items-center justify-center" aria-label="حذف الصورة"><X size={14} /></button>
              </div>
            ))}
            <label data-testid={`venue-upload-${v.id}`} className="w-24 h-24 rounded-lg border border-dashed border-gold/40 flex flex-col items-center justify-center gap-1 text-gold text-[11px] cursor-pointer shrink-0 hover:bg-gold/10 transition-colors">
              <Upload size={18} /> إضافة صورة
              <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && upload(v, e.target.files[0])} />
            </label>
          </div>
          <SlotsEditor venue={v} onSave={(slots) => saveSlots(v, slots)} />
        </div>
      ))}
    </div>
  );
}

function SlotsEditor({ venue, onSave }) {
  const [slots, setSlots] = useState(venue.time_slots || []);
  const up = (i, k, val) => setSlots(slots.map((s, idx) => idx === i ? { ...s, [k]: val } : s));
  const add = () => setSlots([...slots, { id: String(Date.now()), start: "18:00", end: "22:00", active: true }]);
  const del = (i) => setSlots(slots.filter((_, idx) => idx !== i));
  return (
    <div className="border-t border-gold/10 pt-3 space-y-2" data-testid={`slots-${venue.id}`}>
      <p className="text-xs text-gold">أوقات الحفلات المتاحة (يختار الزبون منها فقط)</p>
      {slots.map((s, i) => (
        <div key={i} className="flex items-center gap-2 flex-wrap">
          <input type="time" data-testid={`slot-start-${venue.id}-${i}`} value={s.start} onChange={(e) => up(i, "start", e.target.value)} className="card-lux p-2 bg-navy2 text-offwhite text-sm outline-none" />
          <span className="text-muted2 text-xs">إلى</span>
          <input type="time" data-testid={`slot-end-${venue.id}-${i}`} value={s.end} onChange={(e) => up(i, "end", e.target.value)} className="card-lux p-2 bg-navy2 text-offwhite text-sm outline-none" />
          <label className="text-[11px] text-muted2 flex items-center gap-1">مفعّل<Switch checked={s.active !== false} onCheckedChange={(v) => up(i, "active", v)} /></label>
          <button onClick={() => del(i)} className="text-red-400" aria-label="حذف الوقت"><Trash2 size={15} /></button>
        </div>
      ))}
      <div className="flex gap-3 items-center">
        <button onClick={add} data-testid={`slot-add-${venue.id}`} className="text-xs text-gold flex items-center gap-1"><Plus size={13} /> إضافة وقت</button>
        <button onClick={() => onSave(slots)} data-testid={`slot-save-${venue.id}`} className="btn-gold px-4 py-1.5 rounded-full text-xs flex items-center gap-1"><Save size={12} /> حفظ الأوقات</button>
      </div>
      <p className="text-[10px] text-muted2 leading-relaxed">ضع وقتًا واحدًا أو وقتين في اليوم، وغيّرها بين الصيف والشتاء حسب الحاجة.</p>
    </div>
  );
}

// ---------- Exports ----------
const cleanParams = (o) => Object.fromEntries(Object.entries(o).filter(([, v]) => v));
function Exports() {
  const [filt, setFilt] = useState({ status: "", date_from: "", date_to: "", cust_status: "" });
  const items = [["bookings", "الحجوزات"], ["customers", "الزبائن"], ["payments", "الدفعات"], ["activity", "سجل النشاط"]];
  const dl = (k, f) => {
    const params = k === "bookings" ? cleanParams({ status: filt.status, date_from: filt.date_from, date_to: filt.date_to })
      : k === "customers" ? cleanParams({ status: filt.cust_status }) : {};
    return run(() => downloadExport(k, f, params), "تم بدء التنزيل");
  };
  return (
    <div className="space-y-3" data-testid="exports-manage">
      <p className="text-xs text-muted2 leading-relaxed">صدّر بياناتك بصيغة CSV (تفتح في Excel) أو JSON للنسخ الاحتياطي والنقل مستقبلاً.</p>
      <div className="card-lux p-4 space-y-2" data-testid="export-filters">
        <p className="text-xs text-gold">فلترة تصدير الحجوزات (اختياري)</p>
        <select value={filt.status} onChange={(e) => setFilt({ ...filt, status: e.target.value })} data-testid="export-status" className="w-full card-lux p-2.5 bg-navy2 text-offwhite text-sm outline-none">
          <option value="">كل الحالات</option>
          <option value="pending">طلبات جديدة</option>
          <option value="approved">بانتظار العربون</option>
          <option value="receipt_uploaded">مراجعة الوصل</option>
          <option value="confirmed">مؤكدة</option>
          <option value="cancelled">ملغاة</option>
        </select>
        <div className="grid grid-cols-2 gap-2">
          <div><label className="text-[11px] text-muted2 mb-1 block">من تاريخ</label><input type="date" data-testid="export-from" value={filt.date_from} onChange={(e) => setFilt({ ...filt, date_from: e.target.value })} className="w-full card-lux p-2 bg-navy2 text-offwhite text-sm outline-none" /></div>
          <div><label className="text-[11px] text-muted2 mb-1 block">إلى تاريخ</label><input type="date" data-testid="export-to" value={filt.date_to} onChange={(e) => setFilt({ ...filt, date_to: e.target.value })} className="w-full card-lux p-2 bg-navy2 text-offwhite text-sm outline-none" /></div>
        </div>
      </div>
      <div className="card-lux p-4 space-y-2" data-testid="export-cust-filters">
        <p className="text-xs text-gold">فلترة تصدير الزبائن (اختياري)</p>
        <select value={filt.cust_status} onChange={(e) => setFilt({ ...filt, cust_status: e.target.value })} data-testid="export-cust-status" className="w-full card-lux p-2.5 bg-navy2 text-offwhite text-sm outline-none">
          <option value="">كل الحالات</option>
          <option value="حجز مؤكد">حجز مؤكد</option>
          <option value="مهتم ولم يحجز">مهتم ولم يحجز</option>
          <option value="زبون سابق">زبون سابق</option>
          <option value="حجز ملغى">حجز ملغى</option>
        </select>
      </div>
      {items.map(([k, l]) => (
        <div key={k} className="card-lux p-4 flex items-center justify-between">
          <span className="text-offwhite text-sm">{l}</span>
          <div className="flex gap-2">
            <button onClick={() => dl(k, "csv")} data-testid={`export-${k}-csv`} className="px-4 py-2 rounded-full text-xs card-lux text-gold border border-gold/40 hover:bg-gold/10 transition-colors">CSV</button>
            <button onClick={() => dl(k, "json")} data-testid={`export-${k}-json`} className="px-4 py-2 rounded-full text-xs card-lux text-gold border border-gold/40 hover:bg-gold/10 transition-colors">JSON</button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------- Platform link / Instagram ----------
function PlatformLink() {
  const origin = window.location.origin;
  const msg = `أهلاً بك في قمرية 🤍\nسعداء باهتمامك بمناسبتك القادمة.\nلمشاهدة الأماكن والباقات والمواعيد المتاحة، ولإرسال طلب حجزك بسهولة، تفضّل من هنا:\n${origin}\nفريق قمرية بانتظارك ✨`;
  const copy = (t, label) => { navigator.clipboard.writeText(t); toast.success(label); };
  return (
    <div className="space-y-3" data-testid="platform-link">
      <div className="card-lux p-4 space-y-2">
        <p className="text-xs text-gold">رابط المنصة</p>
        <div className="flex items-center justify-between gap-2 bg-navy p-3 rounded-xl">
          <span className="text-sm text-offwhite break-all" dir="ltr">{origin}</span>
          <button onClick={() => copy(origin, "تم نسخ الرابط")} data-testid="copy-link-btn" className="btn-gold px-4 py-2 rounded-full text-xs flex items-center gap-1 shrink-0"><Copy size={13} /> نسخ</button>
        </div>
      </div>
      <div className="card-lux p-4 space-y-2">
        <p className="text-xs text-gold flex items-center gap-1"><Instagram size={14} /> رسالة الرد الآلي لإنستغرام</p>
        <textarea readOnly value={msg} rows={6} className="w-full card-lux p-3 bg-navy2 text-offwhite text-sm outline-none resize-none leading-relaxed" data-testid="ig-message" />
        <button onClick={() => copy(msg, "تم نسخ الرسالة")} data-testid="copy-ig-btn" className="btn-gold w-full py-2.5 rounded-full text-sm flex items-center gap-1 justify-center"><Copy size={14} /> نسخ الرسالة</button>
        <p className="text-[11px] text-muted2 leading-relaxed">انسخ هذه الرسالة وضعها في الرد الآلي (Auto Reply) في إنستغرام. عند فتح الرابط سيظهر للزبون كبطاقة أنيقة تحمل اسم قمرية وصورة فاخرة بدلاً من رابط مجهول.</p>
      </div>
    </div>
  );
}

// ---------- Packages ----------
function Packages() {
  const { data: venues } = useList("admin-venues", "/admin/venues");
  const [venueId, setVenueId] = useState(null);
  const vid = venueId || venues?.[0]?.id;
  const { data, invalidate } = useQuery({ queryKey: ["admin-packages", vid], enabled: !!vid, queryFn: async () => (await api.get("/admin/packages", { params: { venue_id: vid } })).data });
  const qc = useQueryClient();
  const inv = () => qc.invalidateQueries({ queryKey: ["admin-packages", vid] });

  const add = () => run(() => api.post("/admin/packages", { venue_id: vid, name: "باقة جديدة", price: 0, description: "", services: [], order: (data?.length || 0) }), "تمت الإضافة", inv);
  const save = (p) => run(() => api.patch(`/admin/packages/${p.id}`, { name: p.name, price: Number(p.price), description: p.description, services: p.services, visible: p.visible, order: p.order }), "تم الحفظ", inv);
  const del = (id) => window.confirm("حذف الباقة؟") && run(() => api.delete(`/admin/packages/${id}`), "تم الحذف", inv);

  return (
    <div className="space-y-3" data-testid="packages-manage">
      <VenuePicker venues={venues} vid={vid} setVenueId={setVenueId} />
      {(data || []).map((p) => <PackageEditor key={p.id} pkg={p} onSave={save} onDelete={del} />)}
      <button onClick={add} className="btn-gold w-full py-2.5 rounded-full text-sm flex items-center justify-center gap-1"><Plus size={16} /> إضافة باقة</button>
    </div>
  );
}
function PackageEditor({ pkg, onSave, onDelete }) {
  const [p, setP] = useState({ ...pkg, services: pkg.services.join("\n") });
  return (
    <div className="card-lux p-4 space-y-2">
      <Field val={p.name} on={(v) => setP({ ...p, name: v })} placeholder="اسم الباقة" />
      <Field val={p.price} on={(v) => setP({ ...p, price: v.replace(/[^0-9]/g, "") })} placeholder="السعر" />
      <Field val={p.description} on={(v) => setP({ ...p, description: v })} placeholder="الوصف" />
      <textarea value={p.services} onChange={(e) => setP({ ...p, services: e.target.value })} rows={4} placeholder="الخدمات (كل خدمة بسطر)" className="w-full card-lux p-2.5 bg-navy2 text-offwhite text-sm outline-none resize-none" />
      <div className="flex items-center justify-between">
        <label className="flex items-center gap-2 text-xs text-muted2">ظاهرة <Switch checked={p.visible} onCheckedChange={(v) => setP({ ...p, visible: v })} /></label>
        <div className="flex gap-2">
          <button onClick={() => onSave({ ...p, services: p.services.split("\n").map((s) => s.trim()).filter(Boolean) })} className="btn-gold px-4 py-2 rounded-full text-xs flex items-center gap-1"><Save size={13} /> حفظ</button>
          <button onClick={() => onDelete(p.id)} className="text-red-400 px-3"><Trash2 size={16} /></button>
        </div>
      </div>
    </div>
  );
}

// ---------- Addons ----------
function Addons() {
  const { data: venues } = useList("admin-venues", "/admin/venues");
  const [venueId, setVenueId] = useState(null);
  const vid = venueId || venues?.[0]?.id;
  const { data } = useQuery({ queryKey: ["admin-addons", vid], enabled: !!vid, queryFn: async () => (await api.get("/admin/addons", { params: { venue_id: vid } })).data });
  const qc = useQueryClient();
  const inv = () => qc.invalidateQueries({ queryKey: ["admin-addons", vid] });
  const add = () => run(() => api.post("/admin/addons", { venue_id: vid, name: "إضافة جديدة", description: "", price: 0 }), "تمت الإضافة", inv);
  const save = (a) => run(() => api.patch(`/admin/addons/${a.id}`, { name: a.name, description: a.description, price: Number(a.price), visible: a.visible }), "تم الحفظ", inv);
  const del = (id) => window.confirm("حذف؟") && run(() => api.delete(`/admin/addons/${id}`), "تم الحذف", inv);
  return (
    <div className="space-y-3" data-testid="addons-manage">
      <VenuePicker venues={venues} vid={vid} setVenueId={setVenueId} />
      {(data || []).map((a) => <AddonEditor key={a.id} addon={a} onSave={save} onDelete={del} />)}
      <button onClick={add} className="btn-gold w-full py-2.5 rounded-full text-sm flex items-center justify-center gap-1"><Plus size={16} /> إضافة</button>
    </div>
  );
}
function AddonEditor({ addon, onSave, onDelete }) {
  const [a, setA] = useState(addon);
  return (
    <div className="card-lux p-4 space-y-2">
      <Field val={a.name} on={(v) => setA({ ...a, name: v })} placeholder="الاسم" />
      <Field val={a.description} on={(v) => setA({ ...a, description: v })} placeholder="الوصف" />
      <Field val={a.price} on={(v) => setA({ ...a, price: v.replace(/[^0-9]/g, "") })} placeholder="السعر" />
      <div className="flex items-center justify-between">
        <label className="flex items-center gap-2 text-xs text-muted2">ظاهرة <Switch checked={a.visible} onCheckedChange={(v) => setA({ ...a, visible: v })} /></label>
        <div className="flex gap-2">
          <button onClick={() => onSave(a)} className="btn-gold px-4 py-2 rounded-full text-xs flex items-center gap-1"><Save size={13} /> حفظ</button>
          <button onClick={() => onDelete(a.id)} className="text-red-400 px-3"><Trash2 size={16} /></button>
        </div>
      </div>
    </div>
  );
}

// ---------- Terms ----------
function Terms() {
  const { data, invalidate } = useList("admin-terms", "/admin/terms");
  const add = () => run(() => api.post("/admin/terms", { text: "شرط جديد", order: (data?.length || 0), visible: true }), "تمت الإضافة", invalidate);
  const save = (t) => run(() => api.patch(`/admin/terms/${t.id}`, { text: t.text, visible: t.visible }), "تم الحفظ", invalidate);
  const del = (id) => window.confirm("حذف الشرط؟") && run(() => api.delete(`/admin/terms/${id}`), "تم الحذف", invalidate);
  return (
    <div className="space-y-3" data-testid="terms-manage">
      {(data || []).map((t, i) => <TermEditor key={t.id} idx={i} term={t} onSave={save} onDelete={del} />)}
      <button onClick={add} className="btn-gold w-full py-2.5 rounded-full text-sm flex items-center justify-center gap-1"><Plus size={16} /> إضافة شرط</button>
    </div>
  );
}
function TermEditor({ term, idx, onSave, onDelete }) {
  const [t, setT] = useState(term);
  return (
    <div className="card-lux p-4 space-y-2">
      <div className="flex items-start gap-2">
        <span className="w-6 h-6 rounded-full bg-gold/15 text-gold flex items-center justify-center text-xs shrink-0 mt-1">{idx + 1}</span>
        <textarea value={t.text} onChange={(e) => setT({ ...t, text: e.target.value })} rows={3} className="flex-1 card-lux p-2.5 bg-navy2 text-offwhite text-sm outline-none resize-none" />
      </div>
      <div className="flex items-center justify-between">
        <label className="flex items-center gap-2 text-xs text-muted2">ظاهر <Switch checked={t.visible} onCheckedChange={(v) => setT({ ...t, visible: v })} /></label>
        <div className="flex gap-2">
          <button onClick={() => onSave(t)} className="btn-gold px-4 py-2 rounded-full text-xs flex items-center gap-1"><Save size={13} /> حفظ</button>
          <button onClick={() => onDelete(t.id)} className="text-red-400 px-3"><Trash2 size={16} /></button>
        </div>
      </div>
    </div>
  );
}

// ---------- Transfer settings ----------
function TransferSettings() {
  const { data } = useQuery({ queryKey: ["admin-settings"], queryFn: async () => (await api.get("/admin/settings")).data });
  const [s, setS] = useState(null);
  const v = s || data || {};
  const save = () => run(() => api.patch("/admin/settings", {
    transfer_bank: v.transfer_bank, transfer_account_name: v.transfer_account_name,
    transfer_card_number: v.transfer_card_number, transfer_instructions: v.transfer_instructions,
    phone: v.phone, whatsapp: v.whatsapp, location: v.location, hero_title: v.hero_title, map_query: v.map_query,
  }), "تم الحفظ");
  const up = (k, val) => setS({ ...v, [k]: val });
  return (
    <div className="card-lux p-5 space-y-3" data-testid="transfer-settings">
      <h2 className="text-sm text-gold">بيانات بطاقة قمرية</h2>
      <Labeled label="اسم المصرف" val={v.transfer_bank} on={(x) => up("transfer_bank", x)} />
      <Labeled label="اسم الحساب" val={v.transfer_account_name} on={(x) => up("transfer_account_name", x)} />
      <Labeled label="رقم البطاقة / الحساب" val={v.transfer_card_number} on={(x) => up("transfer_card_number", x)} />
      <div><label className="text-xs text-muted2 mb-1 block">التعليمات</label>
        <textarea value={v.transfer_instructions || ""} onChange={(e) => up("transfer_instructions", e.target.value)} rows={3} className="w-full card-lux p-2.5 bg-navy2 text-offwhite text-sm outline-none resize-none" /></div>
      <div className="gold-divider my-1" />
      <h2 className="text-sm text-gold">بيانات التواصل والواجهة</h2>
      <Labeled label="عبارة الواجهة الرئيسية" val={v.hero_title} on={(x) => up("hero_title", x)} />
      <Labeled label="رقم الهاتف" val={v.phone} on={(x) => up("phone", x)} />
      <Labeled label="واتساب (مع رمز الدولة)" val={v.whatsapp} on={(x) => up("whatsapp", x)} />
      <Labeled label="الموقع" val={v.location} on={(x) => up("location", x)} />
      <Labeled label="بحث خرائط قوقل (اسم أو إحداثيات الموقع)" val={v.map_query} on={(x) => up("map_query", x)} />
      <button onClick={save} data-testid="save-settings-btn" className="btn-gold w-full py-2.5 rounded-full text-sm flex items-center justify-center gap-1"><Save size={15} /> حفظ الإعدادات</button>
    </div>
  );
}

// ---------- Replies ----------
function Replies({ canEdit }) {
  const { data, invalidate } = useList("admin-replies-all", "/admin/replies");
  const add = () => run(() => api.post("/admin/replies", { text: "رسالة جديدة", active: true }), "تمت الإضافة", invalidate);
  const save = (r) => run(() => api.patch(`/admin/replies/${r.id}`, { text: r.text, active: r.active }), "تم الحفظ", invalidate);
  const del = (id) => window.confirm("حذف؟") && run(() => api.delete(`/admin/replies/${id}`), "تم الحذف", invalidate);
  return (
    <div className="space-y-3" data-testid="replies-manage">
      {(data || []).map((r) => canEdit ? <ReplyEditor key={r.id} reply={r} onSave={save} onDelete={del} /> : (
        <div key={r.id} className="card-lux p-4 text-sm text-offwhite/90">{r.text}</div>
      ))}
      {canEdit && <button onClick={add} className="btn-gold w-full py-2.5 rounded-full text-sm flex items-center justify-center gap-1"><Plus size={16} /> إضافة رسالة</button>}
    </div>
  );
}
function ReplyEditor({ reply, onSave, onDelete }) {
  const [r, setR] = useState(reply);
  return (
    <div className="card-lux p-4 space-y-2">
      <textarea value={r.text} onChange={(e) => setR({ ...r, text: e.target.value })} rows={2} className="w-full card-lux p-2.5 bg-navy2 text-offwhite text-sm outline-none resize-none" />
      <div className="flex items-center justify-between">
        <label className="flex items-center gap-2 text-xs text-muted2">مفعّلة <Switch checked={r.active} onCheckedChange={(v) => setR({ ...r, active: v })} /></label>
        <div className="flex gap-2">
          <button onClick={() => onSave(r)} className="btn-gold px-4 py-2 rounded-full text-xs flex items-center gap-1"><Save size={13} /> حفظ</button>
          <button onClick={() => onDelete(r.id)} className="text-red-400 px-3"><Trash2 size={16} /></button>
        </div>
      </div>
    </div>
  );
}

// ---------- Staff ----------
function Staff() {
  const { data, invalidate } = useList("admin-staff", "/admin/staff");
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "reception" });
  const add = () => run(async () => { await api.post("/admin/staff", form); setForm({ name: "", email: "", password: "", role: "reception" }); }, "تمت الإضافة", invalidate);
  const toggle = (u) => run(() => api.patch(`/admin/staff/${u.id}`, { active: !u.active }), "تم التحديث", invalidate);
  return (
    <div className="space-y-3" data-testid="staff-manage">
      {(data || []).map((u) => (
        <div key={u.id} className="card-lux p-4 flex items-center justify-between">
          <div>
            <p className="text-offwhite font-medium">{u.name}</p>
            <p className="text-xs text-muted2">{u.email} · {u.role === "owner" ? "مدير" : "استقبال"}</p>
          </div>
          {u.role !== "owner" && (
            <button onClick={() => toggle(u)} className={`text-xs px-3 py-1.5 rounded-full flex items-center gap-1 ${u.active ? "text-green-300 bg-green-500/10" : "text-red-300 bg-red-500/10"}`}>
              <Power size={13} /> {u.active ? "مفعّل" : "معطّل"}
            </button>
          )}
        </div>
      ))}
      <div className="card-lux p-4 space-y-2">
        <h2 className="text-sm text-gold">إضافة موظف</h2>
        <Field val={form.name} on={(v) => setForm({ ...form, name: v })} placeholder="الاسم" />
        <Field val={form.email} on={(v) => setForm({ ...form, email: v })} placeholder="البريد الإلكتروني" />
        <Field val={form.password} on={(v) => setForm({ ...form, password: v })} placeholder="كلمة المرور" />
        <button onClick={add} className="btn-gold w-full py-2.5 rounded-full text-sm flex items-center justify-center gap-1"><Plus size={16} /> إضافة</button>
      </div>
    </div>
  );
}

// ---------- Activity ----------
function Activity() {
  const { data } = useQuery({ queryKey: ["activity"], queryFn: async () => (await api.get("/admin/activity")).data, refetchInterval: 8000 });
  return (
    <div className="space-y-2" data-testid="activity-log">
      {(data || []).map((a) => (
        <div key={a.id} className="card-lux p-3 flex items-start gap-3">
          <span className="text-[10px] text-muted2 shrink-0 mt-0.5">{new Date(a.timestamp).toLocaleString("ar-IQ", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "2-digit" })}</span>
          <div>
            <p className="text-sm text-offwhite">{a.user_name} — {a.action}{a.booking_number ? ` (${a.booking_number})` : ""}</p>
            {a.detail && <p className="text-xs text-muted2">{a.detail}</p>}
          </div>
        </div>
      ))}
      {!data?.length && <p className="text-muted2/60 text-sm card-lux p-4">لا يوجد نشاط.</p>}
    </div>
  );
}

// ---------- shared ----------
const VenuePicker = ({ venues, vid, setVenueId }) => (
  <div className="flex gap-2">
    {(venues || []).map((v) => (
      <button key={v.id} onClick={() => setVenueId(v.id)} className={`px-4 py-2 rounded-full text-xs ${vid === v.id ? "btn-gold" : "card-lux text-muted2"}`}>{v.name}</button>
    ))}
  </div>
);
const Field = ({ val, on, placeholder }) => (
  <input value={val || ""} onChange={(e) => on(e.target.value)} placeholder={placeholder} className="w-full card-lux p-2.5 bg-navy2 text-offwhite text-sm outline-none focus:ring-1 focus:ring-gold" />
);
const Labeled = ({ label, val, on }) => (
  <div><label className="text-xs text-muted2 mb-1 block">{label}</label><Field val={val} on={on} /></div>
);
